import { type RecordId } from '../types';

export const TOP = 'TOP';
export const BEFORE = 'BEFORE';
export const AFTER = 'AFTER';
export const BOTTOM = 'BOTTOM';

export type ItemPlacementPositionEnum =
  | typeof TOP
  | typeof BOTTOM
  | typeof BEFORE
  | typeof AFTER;

export interface ItemInterface {
  id: RecordId;
}

export default class ItemPlacement<
  Target extends ItemInterface = ItemInterface
> {
  position: ItemPlacementPositionEnum;
  target?: Target;

  static top<T extends ItemInterface>() {
    return new this<T>(TOP);
  }

  static bottom<T extends ItemInterface>() {
    return new this<T>(BOTTOM);
  }

  static before<T extends ItemInterface>(target: T) {
    return new this(BEFORE, target);
  }

  static after<T extends ItemInterface>(target: T) {
    return new this(AFTER, target);
  }

  constructor(position: ItemPlacementPositionEnum, target?: Target) {
    this.position = position;
    this.target = target;
  }

  isEqual(otherPlacement: ItemPlacement<Target>) {
    if (this.position !== otherPlacement.position) {
      return false;
    }

    return (
      this.target &&
      otherPlacement.target &&
      this.target.id === otherPlacement.target.id
    );
  }

  /**
   * Returns the index to insert items into the list, based on the placement.
   *
   * This index could be used, for example, with Array#splice, to inject the
   * items at the expected location.
   *
   * If the position is before/after, and the item is not found in the list, then
   * the index will be the top/bottom of the list, respecitively.
   */
  indexForList(items: Target[]): number {
    const target = this.target;
    const position = this.position;

    if (!target) {
      return items.length;
    }

    switch (position) {
      case BEFORE:
        return Math.max(0, items.indexOf(target));

      case AFTER:
      default:
        return items.includes(target) ? items.indexOf(target) : items.length;
    }
  }
}
